import React, { useState, useMemo, useEffect, useContext, useCallback, createContext } from 'react'
import { NavLink, Outlet, useLocation } from 'react-router-dom'
import {
    LayoutDashboard,
    Store,
    Palette,
    Menu as MenuIcon,
    Share2,
    LayoutTemplate,
    FileText,
    Newspaper,
    MessageSquareMore,
    Megaphone,
    Search,
    FileSearch,
    ArrowRightLeft,
    Map,
    Plug,
    CreditCard,
    BarChart3,
    MousePointerClick,
    Code2,
    Globe,
    Mail,
    Rocket,
    ChevronDown,
    ExternalLink,
    Circle,
    ChevronLeft,
    ChevronRight,
    X
} from 'lucide-react'
import styles from './MicrositeShell.module.css'
import { BASE_URL_API } from '../../../References/URLs'
import AuthContext from '../../../store/auth-context'
import ActiveStoreContext from '../../../store/ActiveStoreContext'

// ──────────────────────────────────────────────────────────────
// Sidebar configuration — grouped sections, each with nav items.
// "to" doubles as the key used for unsaved-tab tracking below.
// ──────────────────────────────────────────────────────────────
const NAV_GROUPS = [
    {
        label: 'Overview',
        items: [
            { to: '/microsite', label: 'Dashboard', icon: LayoutDashboard, end: true }
        ]
    },
    {
        label: 'Identity',
        items: [
            { to: '/microsite/general', label: 'General', icon: Store },
            { to: '/microsite/theme', label: 'Theme', icon: Palette },
            { to: '/microsite/navigation', label: 'Navigation', icon: MenuIcon },
            { to: '/microsite/social', label: 'Social links', icon: Share2 }
        ]
    },
    {
        label: 'Landing page',
        items: [
            { to: '/microsite/landing', label: 'Page builder', icon: LayoutTemplate }
        ]
    },
    {
        label: 'Content',
        items: [
            { to: '/microsite/pages', label: 'Custom pages', icon: FileText },
            { to: '/microsite/blog', label: 'Blog', icon: Newspaper },
            { to: '/microsite/popups', label: 'Popups', icon: MessageSquareMore },
            { to: '/microsite/announcements', label: 'Announcements', icon: Megaphone }
        ]
    },
    {
        label: 'SEO',
        items: [
            { to: '/microsite/seo', label: 'SEO settings', icon: Search },
            { to: '/microsite/seo/pages', label: 'Page SEO', icon: FileSearch },
            { to: '/microsite/seo/redirects', label: 'Redirects', icon: ArrowRightLeft },
            { to: '/microsite/seo/sitemap', label: 'Sitemap', icon: Map }
        ]
    },
    {
        label: 'Integrations & plan',
        items: [
            { to: '/microsite/integrations', label: 'Integrations', icon: Plug },
            { to: '/microsite/plan', label: 'Plan & limits', icon: CreditCard }
        ]
    },
    {
        label: 'Analytics',
        items: [
            { to: '/microsite/analytics', label: 'Page views', icon: BarChart3 },
            { to: '/microsite/analytics/conversions', label: 'Conversions', icon: MousePointerClick }
        ]
    },
    {
        label: 'Tracking',
        items: [
            { to: '/microsite/tracking', label: 'Tracking scripts', icon: Code2 }
        ]
    },
    {
        label: 'Delivery',
        items: [
            { to: '/microsite/domains', label: 'Domains', icon: Globe },
            { to: '/microsite/email-templates', label: 'Email templates', icon: Mail },
            { to: '/microsite/publish', label: 'Publish', icon: Rocket }
        ]
    }
]

// Flatten for breadcrumb / page-title lookups elsewhere if needed
const ALL_NAV_ITEMS = NAV_GROUPS.flatMap((g) => g.items)

const BUILD_STATUS_META = {
    SUCCESS:  { label: 'Live',            tone: 'live' },
    BUILDING: { label: 'Publishing…',     tone: 'pending' },
    FAILED:   { label: 'Publish failed',  tone: 'failed' },
    PENDING:  { label: 'Not published',   tone: 'pending' }
}

/**
 * MicrositeUnsavedTabsContext
 *
 * NOTE FOR INTEGRATION: this CRM has no existing shared-state
 * mechanism for cross-screen dirty tracking (ActiveStoreContext
 * only exposes the active store, not form state), so this context
 * is created and owned locally by MicrositeShell. It is exported
 * so any screen CAN opt in by calling useUnsavedTab(path, isDirty)
 * to light up its sidebar dot — but no screen currently does this.
 * Wiring each screen's existing `isDirty` state into this hook is
 * a follow-up task, not something this shell can infer on its own.
 */
export const MicrositeUnsavedTabsContext = createContext({
    unsavedTabs: new Set(),
    setTabDirty: () => {},
    micrositeStore: null
})

export const useUnsavedTab = (path, isDirty) => {
    const { setTabDirty } = useContext(MicrositeUnsavedTabsContext)
    useEffect(() => {
        setTabDirty(path, isDirty)
        return () => setTabDirty(path, false)
    }, [path, isDirty, setTabDirty])
}

const MicrositeShell = () => {
    const location = useLocation()
    const authCtx = useContext(AuthContext)
    const asCtx   = useContext(ActiveStoreContext)
    const activeStore = useMemo(() => {
        try {
            return (asCtx?.activeStore && asCtx.activeStore.trim() !== '')
                ? JSON.parse(asCtx.activeStore)
                : null
        } catch {
            return null
        }
    }, [asCtx?.activeStore])

    // Sidebar collapse (persisted across the session for muscle memory)
    const [collapsed, setCollapsed] = useState(() => {
        try {
            return localStorage.getItem('microsite_sidebar_collapsed') === '1'
        } catch {
            return false
        }
    })

    // Mobile drawer open/close
    const [mobileNavOpen, setMobileNavOpen] = useState(false)

    // Which collapsible groups are expanded — all open by default
    const [openGroups, setOpenGroups] = useState(() =>
        NAV_GROUPS.reduce((acc, g) => ({ ...acc, [g.label]: true }), {})
    )

    const [loadingStore, setLoadingStore] = useState(true)
    const [store, setStore] = useState(null)
    const [primaryDomain, setPrimaryDomain] = useState(null)
    const [buildStatus, setBuildStatus] = useState('PENDING')

    // Unsaved tabs — a Set of nav "to" paths with pending changes.
    // See MicrositeUnsavedTabsContext note above re: integration.
    const [unsavedTabs, setUnsavedTabs] = useState(new Set())

    const setTabDirty = useCallback((path, isDirty) => {
        setUnsavedTabs((prev) => {
            const next = new Set(prev)
            if (isDirty) next.add(path)
            else next.delete(path)
            return next
        })
    }, [])

    const unsavedTabsContextValue = useMemo(
        () => ({ unsavedTabs, setTabDirty, micrositeStore: store }),
        [unsavedTabs, setTabDirty, store]
    )

    const post = useCallback(async (resource, payload = {}) => {
        try {
            const res = await fetch(BASE_URL_API + resource, {
                method: 'POST',
                body: JSON.stringify({
                    token: authCtx?.token,
                    storeIdentifier: activeStore?.storeIdentifier,
                    vendorIdentifier: activeStore?.vendorIdentifier,
                    ...payload
                })
            })
            if (!res.ok) return null
            const data = await res.json()
            if (data?.message === 'tokenExpired') {
                authCtx.logout()
                return null
            }
            return data
        } catch {
            return null
        }
    }, [authCtx, activeStore])

    const loadShellData = useCallback(async () => {
        setLoadingStore(true)

        const [generalRes, buildRes, domainsRes] = await Promise.all([
            post('micrositeGetStoreGeneral'),
            post('micrositeGetBuildStatus'),
            post('micrositeGetDomains')
        ])

        if (generalRes?.status === 'success') {
            setStore(generalRes.response || null)
        }

        if (buildRes?.status === 'success') {
            // micrositeGetBuildStatus returns the build record fields
            // directly on `response` (buildStatus, buildLog, builtAt, ...),
            // not nested further.
            setBuildStatus(buildRes.response?.buildStatus || 'PENDING')
        }

        if (domainsRes?.status === 'success') {
            const domains = domainsRes.response?.data ?? []
            const primary = domains.find((d) => d.isPrimary) || null
            setPrimaryDomain(primary)
        }

        setLoadingStore(false)
    }, [post])

    useEffect(() => {
        if (activeStore?.storeIdentifier) loadShellData()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeStore?.storeIdentifier])

    // Poll build status while a build is in progress, so the topbar
    // pill updates without the user needing to revisit MicrositePublish.
    useEffect(() => {
        if (buildStatus !== 'BUILDING') return
        const interval = setInterval(async () => {
            const res = await post('micrositeGetBuildStatus')
            if (res?.status === 'success') {
                setBuildStatus(res.response?.buildStatus || 'PENDING')
            }
        }, 6000)
        return () => clearInterval(interval)
    }, [buildStatus, post])

    useEffect(() => {
        try {
            localStorage.setItem('microsite_sidebar_collapsed', collapsed ? '1' : '0')
        } catch {
            // localStorage unavailable — ignore silently
        }
    }, [collapsed])

    // Close the mobile drawer whenever the route changes
    useEffect(() => {
        setMobileNavOpen(false)
    }, [location.pathname])

    const toggleGroup = (label) => {
        setOpenGroups((prev) => ({ ...prev, [label]: !prev[label] }))
    }

    // Prefer a verified custom domain if one is set as primary;
    // otherwise fall back to the free Lokaci subdomain.
    const previewUrl = useMemo(() => {
        if (primaryDomain?.domain) return `https://${primaryDomain.domain}`
        if (store?.subdomain) return `https://${store.subdomain}.lokacipro.com`
        return null
    }, [primaryDomain, store?.subdomain])

    const buildStatusMeta = BUILD_STATUS_META[buildStatus] || BUILD_STATUS_META.PENDING

    const displaySubdomainLabel = primaryDomain?.domain
        ? primaryDomain.domain
        : store?.subdomain
            ? `${store.subdomain}.lokacipro.com`
            : '—'

    return (
        <MicrositeUnsavedTabsContext.Provider value={unsavedTabsContextValue}>
            <div className={styles.shell}>
                {/* ── Sidebar (desktop) ─────────────────────────────── */}
                <aside
                    className={`${styles.sidebar} ${collapsed ? styles.sidebarCollapsed : ''}`}
                    aria-label="Microsite settings navigation"
                >
                    <div className={styles.sidebarHeader}>
                        {!collapsed && <span className={styles.sidebarHeaderLabel}>Microsite</span>}
                        <button
                            type="button"
                            className={styles.collapseBtn}
                            onClick={() => setCollapsed((c) => !c)}
                            aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
                            title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
                        >
                            {collapsed ? <ChevronRight size={16} /> : <ChevronLeft size={16} />}
                        </button>
                    </div>

                    <nav className={styles.nav}>
                        {NAV_GROUPS.map((group) => (
                            <div key={group.label} className={styles.navGroup}>
                                {!collapsed && (
                                    <button
                                        type="button"
                                        className={styles.navGroupLabel}
                                        onClick={() => toggleGroup(group.label)}
                                        aria-expanded={openGroups[group.label]}
                                    >
                                        <span>{group.label}</span>
                                        <ChevronDown
                                            size={13}
                                            className={`${styles.groupChevron} ${
                                                openGroups[group.label] ? styles.groupChevronOpen : ''
                                            }`}
                                        />
                                    </button>
                                )}

                                {(collapsed || openGroups[group.label]) && (
                                    <ul className={styles.navList}>
                                        {group.items.map((item) => {
                                            const Icon = item.icon
                                            const isDirty = unsavedTabs.has(item.to)
                                            return (
                                                <li key={item.to}>
                                                    <NavLink
                                                        to={item.to}
                                                        end={item.end}
                                                        className={({ isActive }) =>
                                                            `${styles.navItem} ${isActive ? styles.navItemActive : ''}`
                                                        }
                                                        title={collapsed ? item.label : undefined}
                                                    >
                                                        <Icon size={16} className={styles.navItemIcon} strokeWidth={1.75} />
                                                        {!collapsed && <span className={styles.navItemLabel}>{item.label}</span>}
                                                        {isDirty && (
                                                            <span
                                                                className={styles.unsavedDot}
                                                                aria-label="Unsaved changes"
                                                                title="Unsaved changes"
                                                            />
                                                        )}
                                                    </NavLink>
                                                </li>
                                            )
                                        })}
                                    </ul>
                                )}
                            </div>
                        ))}
                    </nav>
                </aside>

                {/* ── Mobile nav drawer ─────────────────────────────── */}
                {mobileNavOpen && (
                    <>
                        <button
                            type="button"
                            className={styles.mobileOverlay}
                            onClick={() => setMobileNavOpen(false)}
                            aria-label="Close navigation"
                        />
                        <aside className={styles.mobileDrawer} aria-label="Microsite settings navigation">
                            <div className={styles.mobileDrawerHeader}>
                                <span className={styles.sidebarHeaderLabel}>Microsite</span>
                                <button
                                    type="button"
                                    className={styles.collapseBtn}
                                    onClick={() => setMobileNavOpen(false)}
                                    aria-label="Close navigation"
                                >
                                    <X size={18} />
                                </button>
                            </div>
                            <nav className={styles.nav}>
                                {NAV_GROUPS.map((group) => (
                                    <div key={group.label} className={styles.navGroup}>
                                        <div className={styles.navGroupLabelStatic}>{group.label}</div>
                                        <ul className={styles.navList}>
                                            {group.items.map((item) => {
                                                const Icon = item.icon
                                                const isDirty = unsavedTabs.has(item.to)
                                                return (
                                                    <li key={item.to}>
                                                        <NavLink
                                                            to={item.to}
                                                            end={item.end}
                                                            className={({ isActive }) =>
                                                                `${styles.navItem} ${isActive ? styles.navItemActive : ''}`
                                                            }
                                                        >
                                                            <Icon size={16} className={styles.navItemIcon} strokeWidth={1.75} />
                                                            <span className={styles.navItemLabel}>{item.label}</span>
                                                            {isDirty && <span className={styles.unsavedDot} />}
                                                        </NavLink>
                                                    </li>
                                                )
                                            })}
                                        </ul>
                                    </div>
                                ))}
                            </nav>
                        </aside>
                    </>
                )}

                {/* ── Main column: top bar + page outlet ───────────── */}
                <div className={styles.main}>
                    <header className={styles.topBar}>
                        <div className={styles.topBarLeft}>
                            <button
                                type="button"
                                className={styles.mobileMenuBtn}
                                onClick={() => setMobileNavOpen(true)}
                                aria-label="Open navigation"
                            >
                                <MenuIcon size={18} />
                            </button>

                            <div className={styles.storeIdentity}>
                                <span className={styles.storeName}>
                                    {loadingStore ? '—' : store?.storeName || 'Untitled store'}
                                </span>
                                <span className={styles.subdomainBadge}>
                                    {loadingStore ? '—' : displaySubdomainLabel}
                                </span>
                            </div>

                            <span className={`${styles.statusPill} ${styles[`statusPill_${buildStatusMeta.tone}`]}`}>
                                <Circle size={7} className={styles.statusDot} fill="currentColor" />
                                {loadingStore ? 'Loading…' : buildStatusMeta.label}
                            </span>
                        </div>

                        <div className={styles.topBarRight}>
                            {previewUrl && (
                                <a
                                    href={previewUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className={styles.previewLink}
                                >
                                    Preview site
                                    <ExternalLink size={13} />
                                </a>
                            )}
                        </div>
                    </header>

                    <main className={styles.pageOutlet}>
                        <Outlet context={{ store, setStore, unsavedTabs, refreshShellData: loadShellData }} />
                    </main>
                </div>
            </div>
        </MicrositeUnsavedTabsContext.Provider>
    )
}

export default MicrositeShell